import { useState, useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";

export interface SavedNursingHome {
  nh_slug: string;
  center_name: string;
  county_slug: string;
  county_name: string;
  savedAt: string;
  isGuestSave: boolean;
}

const GUEST_SAVES_KEY = "grandpal_guest_saves";

export function useGuestSaves() {
  const { user } = useAuth();
  const [guestSaves, setGuestSaves] = useState<SavedNursingHome[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load guest saves from localStorage on mount
  useEffect(() => {
    console.log("Loading guest saves from localStorage...");
    const saved = localStorage.getItem(GUEST_SAVES_KEY);
    console.log("Raw localStorage data:", saved);
    
    if (saved) {
      try {
        const parsedSaves = JSON.parse(saved);
        console.log("Parsed saves:", parsedSaves);
        setGuestSaves(parsedSaves);
      } catch (error) {
        console.error("Error parsing guest saves:", error);
        localStorage.removeItem(GUEST_SAVES_KEY);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage whenever guestSaves changes (but only after initial load)
  useEffect(() => {
    if (isLoaded) {
      console.log("Saving to localStorage:", {
        count: guestSaves.length,
        saves: guestSaves.map(s => ({ name: s.center_name, slug: s.nh_slug }))
      });
      try {
        localStorage.setItem(GUEST_SAVES_KEY, JSON.stringify(guestSaves));
        
        // Verify it was saved correctly
        const verified = localStorage.getItem(GUEST_SAVES_KEY);
        if (verified) {
          const parsed = JSON.parse(verified);
          console.log("Verified localStorage save - count:", parsed.length);
          if (parsed.length !== guestSaves.length) {
            console.error("localStorage save verification failed!", {
              expected: guestSaves.length,
              actual: parsed.length
            });
          }
        }
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    }
  }, [guestSaves, isLoaded]);

  const isNursingHomeSaved = (nhSlug: string): boolean => {
    return guestSaves.some(save => save.nh_slug === nhSlug);
  };

  const saveNursingHome = (nursingHome: {
    nh_slug: string;
    center_name: string;
    county_slug: string;
    county_name?: string;
  }) => {
    const savedHome: SavedNursingHome = {
      nh_slug: nursingHome.nh_slug,
      center_name: nursingHome.center_name,
      county_slug: nursingHome.county_slug,
      county_name: nursingHome.county_name || nursingHome.county_slug,
      savedAt: new Date().toISOString(),
      isGuestSave: !user
    };

    console.log("saveNursingHome called for:", nursingHome.nh_slug);
    
    // Use functional update to ensure we get the latest state
    setGuestSaves(currentSaves => {
      console.log("saveNursingHome - current saves:", currentSaves.length, currentSaves);
      
      // Check if already exists
      const existingIndex = currentSaves.findIndex(save => save.nh_slug === nursingHome.nh_slug);
      if (existingIndex !== -1) {
        console.log("Item already exists, not adding duplicate");
        return currentSaves;
      }
      
      const newSaves = [...currentSaves, savedHome];
      console.log("saveNursingHome - new saves array:", newSaves.length, newSaves);
      return newSaves;
    });
  };

  const removeSavedNursingHome = (nhSlug: string) => {
    setGuestSaves(prev => prev.filter(save => save.nh_slug !== nhSlug));
  };

  const getSavedCount = (): number => {
    console.log("getSavedCount called, current length:", guestSaves.length);
    return guestSaves.length;
  };

  const clearAllSaves = () => {
    setGuestSaves([]);
    localStorage.removeItem(GUEST_SAVES_KEY);
  };

  // Migrate guest saves to user account when they log in
  const migrateGuestSavesToUser = async () => {
    if (!user || guestSaves.length === 0) return;

    // This could be expanded to sync with backend user saves
    // For now, we just mark them as user saves
    setGuestSaves(prev => 
      prev.map(save => ({ ...save, isGuestSave: false }))
    );
  };

  // Auto-migrate when user logs in
  useEffect(() => {
    if (user && guestSaves.some(save => save.isGuestSave)) {
      migrateGuestSavesToUser();
    }
  }, [user]);

  return {
    guestSaves,
    isNursingHomeSaved,
    saveNursingHome,
    removeSavedNursingHome,
    getSavedCount,
    clearAllSaves,
    migrateGuestSavesToUser
  };
}